import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ShieldX, ShieldAlert, Loader2 } from "lucide-react";
import { API } from "@/lib/api";

const fmt = (v) => (v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");
const TONE = {
  Valid: { icon: ShieldCheck, cls: "text-emerald-600 bg-emerald-50 border-emerald-200", text: "This certificate is valid." },
  Reissued: { icon: ShieldAlert, cls: "text-amber-600 bg-amber-50 border-amber-200", text: "This certificate was replaced by a newer certificate." },
  Revoked: { icon: ShieldX, cls: "text-rose-600 bg-rose-50 border-rose-200", text: "This certificate has been revoked and is no longer valid." },
};

export default function VerifyCertificate() {
  const { number } = useParams();
  const [sp] = useSearchParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    const t = sp.get("t");
    axios.get(`${API}/public/verify-certificate/${encodeURIComponent(number)}${t ? `?t=${encodeURIComponent(t)}` : ""}`)
      .then(({ data }) => setD(data)).catch((e) => setErr(e?.response?.data?.detail || "Could not verify this certificate."));
  }, [number, sp]);

  const tone = d && (TONE[d.status] || TONE.Revoked);
  const rows = d && [
    ["Certificate Number", d.certificate_number], ["Partner Type", d.partner_type],
    ...(d.limited ? [] : [["Partner Name", d.partner_name], [d.partner_type === "Delivery Partner" ? "Connect Tym ID" : "Franchise ID", d.partner_code],
      ["City and State", [d.city, d.state].filter(Boolean).join(", ") || "-"], ["Verification", d.verified ? "Verified" : "Verification pending"]]),
    ["Issue Date", fmt(d.issue_date)], ["Current Status", d.status],
    ...(d.reissued_to ? [["Replaced by", d.reissued_to]] : []),
  ];
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 p-4 text-slate-900" data-testid="verify-page">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
        <div className="flex items-center gap-3 bg-gradient-to-r from-blue-700 to-emerald-600 px-6 py-5 text-white">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20 font-head text-lg font-black">C</div>
          <div><p className="font-head text-lg font-bold">{d?.company_name || "Connect Tym"}</p><p className="text-xs text-white/80">Certificate Verification</p></div>
        </div>
        <div className="p-6">
          {!d && !err && <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Checking certificate…</p>}
          {err && <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-600" data-testid="verify-error"><ShieldX className="mb-1 h-6 w-6" />{err}</div>}
          {d && (
            <>
              <div className={`flex items-center gap-3 rounded-xl border p-4 ${tone.cls}`} data-testid="verify-status">
                <tone.icon className="h-8 w-8 shrink-0" /><div><p className="font-head text-lg font-bold">{d.status}</p><p className="text-sm">{tone.text}</p></div>
              </div>
              <dl className="mt-5 divide-y divide-slate-100 text-sm">
                {rows.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 py-2.5"><dt className="text-slate-500">{k}</dt><dd className="text-right font-semibold" data-testid={`verify-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v}</dd></div>
                ))}
              </dl>
              {d.limited && <p className="mt-4 text-xs text-slate-500" data-testid="verify-limited">Scan the QR code on the certificate to see the partner's details.</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
